import { useState, useEffect, useCallback } from 'react';
import { menuSchedule } from 'data/data';

interface RestaurantStatus {
  text: 'Open' | 'Closed';
  color: string;
  isOpen: boolean;
}

interface UseRestaurantStatusProps {
  menuScheduleData: menuSchedule[] | null;
  updateInterval?: number; // in milliseconds, default 60000 (1 minute)
}

// Utility functions that can be used independently
export const isOpenNow = (fromTime: string, toTime: string): boolean => {
  if (!fromTime || !toTime) return false;

  const now = new Date();
  const [fromHours, fromMinutes] = fromTime.split(":").map(Number);
  const [toHours, toMinutes] = toTime.split(":").map(Number);

  const fromDate = new Date(now);
  fromDate.setHours(fromHours, fromMinutes, 0, 0);

  const toDate = new Date(now);
  toDate.setHours(toHours, toMinutes, 0, 0);

  // Handle overnight shift (e.g., 22:00 to 02:00)
  if (toDate <= fromDate) {
    toDate.setDate(toDate.getDate() + 1);
  }

  return now >= fromDate && now <= toDate;
};

export const getCurrentDay = (): string => {
  const daysOfWeek = [
    "Sunday",
    "Monday", 
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const currentDayIndex = new Date().getDay();
  return daysOfWeek[currentDayIndex];
};

export const calculateRestaurantStatus = (menuScheduleData: menuSchedule[] | null): RestaurantStatus => {
  const currentDay = getCurrentDay();
  if (!menuScheduleData) return { text: "Closed", color: "red", isOpen: false };

  const todaySchedules = menuScheduleData.filter(
    (schedule) => schedule.menuDay === currentDay
  );

  const isCurrentlyOpen = todaySchedules.some((schedule) =>
    isOpenNow(schedule.fromTime, schedule.toTime)
  );

  return {
    text: isCurrentlyOpen ? "Open" : "Closed",
    color: isCurrentlyOpen ? "rgb(123, 181, 66)" : "red",
    isOpen: isCurrentlyOpen,
  };
};

export const useRestaurantStatus = ({ 
  menuScheduleData, 
  updateInterval = 60000 
}: UseRestaurantStatusProps): RestaurantStatus => {
  const [status, setStatus] = useState<RestaurantStatus>({ text: 'Closed', color: 'red', isOpen: false });

  const calculateStatus = useCallback((): RestaurantStatus => {
    return calculateRestaurantStatus(menuScheduleData);
  }, [menuScheduleData]);

  // Update status immediately when component mounts or menuScheduleData changes
  useEffect(() => {
    setStatus(calculateStatus());
  }, [calculateStatus]);

  // Set up interval to check status periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setStatus(calculateStatus());
    }, updateInterval);

    return () => clearInterval(interval);
  }, [calculateStatus, updateInterval]);

  return status;
};